import { useCallback, useEffect, useMemo, useState } from 'react';
import QRCode from 'qrcode.react';
import {
  BiDownload,
  Box,
  Button,
  ControllerAlert,
  Link,
  Stack,
  Text,
  Typography,
} from '@bymason-private/legacy-pattern-components';
import { Route, RouterLink, useRouter } from '@bymason-private/route-provider';
import { ProvisionQRCodeModel } from '@bymason/controller-api-hooks';
import { formatLongDateTimeString } from '@bymason/utils';
import canvasToImage from 'canvas-to-image';

import { LegacyDetailsList, SensitiveText } from '../../';
import ProvisionedDeviceList from './provisioned-device-list';

interface ProvisionViewProps {
  groupDetailsViewRoute: Route;
  devicesViewRoute: Route;
}

export function ProvisionView({ groupDetailsViewRoute, devicesViewRoute }: ProvisionViewProps) {
  const { navigate } = useRouter();
  const [qrCodeData, setQrCodeData] = useState<ProvisionQRCodeModel>();
  const [provisionSessionId, setProvisionSessionId] = useState<string>();

  const detailItems = useMemo(() => {
    if (!qrCodeData) return [];
    const groupLink = (
      <RouterLink
        linkComponent={<Link>{qrCodeData.group.name}</Link>}
        route={groupDetailsViewRoute}
        state={{ name: qrCodeData.group.name }}
      />
    );

    return [
      { label: 'Group', value: groupLink },
      {
        label: 'Device model',
        value: qrCodeData.group.deviceFamily ? qrCodeData.group.deviceFamily : 'Unknown',
      },
      { label: 'Time zone', value: qrCodeData.tz },
      { label: 'Expires', value: formatLongDateTimeString(qrCodeData.expirationDate) },
      { label: 'Wi-Fi', value: qrCodeData.wifi ? qrCodeData.wifi : 'Not specified' },
      {
        label: 'Wi-Fi password',
        value: qrCodeData.pw ? <SensitiveText>{qrCodeData.pw}</SensitiveText> : 'Not specified',
      },
      { label: 'Wi-Fi auth type', value: qrCodeData.type ? qrCodeData.type : 'Not specified' },
    ];
  }, [groupDetailsViewRoute, qrCodeData]);

  const handleQrCodeDownload = useCallback(() => {
    const qrCodeSvg = document.getElementById('provisionDevicesQRCode');

    if (qrCodeSvg) {
      canvasToImage(qrCodeSvg, {
        name: `QRCodeProvisionSession-${provisionSessionId ?? ''}`,
        type: 'png',
        quality: 1,
      });
    }
  }, [provisionSessionId]);

  useEffect(() => {
    if (history.state?.qrCodeData && history.state?.provisionSessionId) {
      setQrCodeData(history.state.qrCodeData as ProvisionQRCodeModel);
      setProvisionSessionId(history.state.provisionSessionId as string);
    } else {
      navigate(devicesViewRoute);
    }
  }, [devicesViewRoute, navigate]);

  useEffect(() => {
    return () => {
      window.history.replaceState({}, document.title);
      // destroy state on navigation
    };
  });

  return (
    <Box>
      <Box display='flex' height='100%' flexDirection='column'>
        <Box flexDirection='column' sx={{ display: 'flex', gap: 1 }}>
          <ControllerAlert
            sx={{
              width: '100%',
            }}
            variant='outlined'
            severity='warning'
          >
            Not supported on{' '}
            <Link
              href='https://docs.bymason.com/mason-os/release-notes/os2'
              target='_blank'
              rel='noopener noreferrer'
              sx={{ color: 'currentcolor' }}
            >
              Mason OS 2.x
            </Link>
          </ControllerAlert>
          <ControllerAlert
            sx={{
              width: '100%',
            }}
            variant='outlined'
            severity='info'
          >
            Closing or refreshing this page will end the session
          </ControllerAlert>

          <Text variant='subtitle1' fontSize={14}>
            Turn on your device and follow the screen prompts to provision with the QR code. Point the camera
            at the code. The device will vibrate when the scan is successful.
          </Text>
        </Box>
        <Box sx={{ display: 'flex' }}>
          <Stack spacing={1}>
            <QRCode
              id='provisionDevicesQRCode'
              includeMargin
              level='M'
              size={340}
              style={{ border: '16px solid #FFF', width: 340 }}
              value={JSON.stringify({ ...qrCodeData, group: qrCodeData?.group.name })}
            />
            <Button
              onClick={handleQrCodeDownload}
              color='info'
              variant='outlined'
              startIcon={<BiDownload size={16} />}
              sx={{ px: 2 }}
            >
              Download QR code
            </Button>
          </Stack>
          <Box sx={{ width: '100%', mt: 4 }}>
            <Typography fontWeight='bold'>QR code information</Typography>
            <LegacyDetailsList hideIconsColumn items={detailItems} />
          </Box>
        </Box>
        {provisionSessionId && <ProvisionedDeviceList sessionId={provisionSessionId} />}
      </Box>
    </Box>
  );
}
